import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface Category {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  children: Category[];
}

interface VariantRow {
  id?: string;
  sku: string;
  barcode: string;
  // BE responses are camelCase but the form uses snake_case for legacy reasons —
  // we coerce in normalizeDetail() below to keep the rest of the form unchanged.
  cost_price: string;
  sell_price: string;
  wholesale_price: string;
  serial_tracked: boolean;
  is_active: boolean;
}

interface VariantBE {
  id: string;
  sku: string;
  barcode?: string;
  costPrice: string;
  sellPrice: string;
  wholesalePrice: string;
  serialTracked: boolean;
  isActive: boolean;
}

interface ProductBE {
  id: string;
  name: string;
  brand?: string;
  categoryId?: string;
  description?: string;
  warrantyMonths: number;
  isActive: boolean;
}

// BE returns `{product, variants}`; flatten + snake_case adapter keeps the form code stable.
interface ProductDetail {
  id: string;
  name: string;
  brand: string;
  category_id: string;
  description: string;
  warranty_months: number;
  is_active: boolean;
  variants: (VariantRow & { id: string })[];
}

function normalizeDetail(raw: { product: ProductBE; variants: VariantBE[] }): ProductDetail {
  return {
    id: raw.product.id,
    name: raw.product.name,
    brand: raw.product.brand ?? "",
    category_id: raw.product.categoryId ?? "",
    description: raw.product.description ?? "",
    warranty_months: raw.product.warrantyMonths,
    is_active: raw.product.isActive,
    variants: raw.variants.map((v) => ({
      id: v.id,
      sku: v.sku,
      barcode: v.barcode ?? "",
      cost_price: v.costPrice,
      sell_price: v.sellPrice,
      wholesale_price: v.wholesalePrice,
      serial_tracked: v.serialTracked,
      is_active: v.isActive,
    })),
  };
}

function flattenCategories(cats: Category[]): { id: string; name: string }[] {
  const out: { id: string; name: string }[] = [];
  function walk(list: Category[], prefix = "") {
    for (const c of list) {
      out.push({ id: c.id, name: prefix + c.name });
      if (c.children?.length) walk(c.children, prefix + "  ");
    }
  }
  walk(cats);
  return out;
}

function emptyVariant(): VariantRow {
  return { sku: "", barcode: "", cost_price: "", sell_price: "", wholesale_price: "", serial_tracked: false, is_active: true };
}

export function ProductFormPage() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: cats } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiGet<Category[]>("/v1/categories"),
  });

  const flatCats = cats ? flattenCategories(cats) : [];

  const { data: existing } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => {
      const raw = await apiGet<{ product: ProductBE; variants: VariantBE[] }>(
        `/v1/products/${id}`,
      );
      return normalizeDetail(raw);
    },
    enabled: isEdit,
  });

  const [name, setName] = useState(existing?.name ?? "");
  const [brand, setBrand] = useState(existing?.brand ?? "");
  const [categoryId, setCategoryId] = useState(existing?.category_id ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [warrantyMonths, setWarrantyMonths] = useState(String(existing?.warranty_months ?? 12));
  const [isActive, setIsActive] = useState(existing?.is_active ?? true);
  const [variants, setVariants] = useState<VariantRow[]>(
    existing?.variants?.length ? existing.variants : [emptyVariant()],
  );
  const [error, setError] = useState("");

  // useState initial values run once; the query resolves later, so seed the
  // form fields when `existing` arrives (only on first arrival to avoid wiping
  // user edits if the query refetches in the background).
  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setBrand(existing.brand);
    setCategoryId(existing.category_id);
    setDescription(existing.description);
    setWarrantyMonths(String(existing.warranty_months));
    setIsActive(existing.is_active);
    setVariants(existing.variants.length ? existing.variants : [emptyVariant()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id]);

  const mutation = useMutation({
    mutationFn: (payload: unknown) =>
      isEdit
        ? apiPatch(`/v1/products/${id}`, payload)
        : apiPost("/v1/products", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void navigate({ to: "/products" });
    },
    onError: (e) => setError(translateApiError(e)),
  });

  // Soft-delete (is_active=FALSE) — manager/admin only. Keeps sales history.
  const user = useAuthStore((s) => s.user);
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const deleteMut = useMutation({
    mutationFn: () => apiDelete(`/v1/products/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void navigate({ to: "/products" });
    },
    onError: (e) => setError(translateApiError(e)),
  });

  function handleDelete() {
    if (window.confirm("Xóa sản phẩm này?")) {
      setError("");
      deleteMut.mutate();
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    mutation.mutate({
      name,
      brand,
      category_id: categoryId || undefined,
      description,
      warranty_months: Number(warrantyMonths),
      is_active: isActive,
      variants: variants.map((v) => ({
        ...v,
        cost_price: Number(v.cost_price),
        sell_price: Number(v.sell_price),
        wholesale_price: Number(v.wholesale_price),
      })),
    });
  }

  function updateVariant(i: number, field: keyof VariantRow, val: unknown) {
    setVariants((prev) => prev.map((v, idx) => (idx === i ? { ...v, [field]: val } : v)));
  }

  function addVariant() {
    setVariants((prev) => [...prev, emptyVariant()]);
  }

  function removeVariant(i: number) {
    setVariants((prev) => prev.filter((_, idx) => idx !== i));
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold">{isEdit ? "Sửa sản phẩm" : "Thêm sản phẩm"}</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader><h2 className="text-base font-medium">Thông tin chung</h2></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Tên sản phẩm *</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="brand">Thương hiệu</Label>
              <Input id="brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat">Danh mục</Label>
              <select
                id="cat"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">-- Chọn danh mục --</option>
                {flatCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="warranty">Bảo hành (tháng)</Label>
              <Input id="warranty" type="number" min={0} value={warrantyMonths} onChange={(e) => setWarrantyMonths(e.target.value)} />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="desc">Mô tả</Label>
              <textarea
                id="desc"
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input id="active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4" />
              <Label htmlFor="active">Đang bán</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h2 className="text-base font-medium">Variants / SKU</h2>
            <Button type="button" variant="outline" size="sm" onClick={addVariant}>
              <Plus size={14} /> Thêm variant
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {variants.map((v, i) => (
              <div key={i} className="grid gap-2 rounded-md border p-3 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label>SKU</Label>
                  <Input value={v.sku} onChange={(e) => updateVariant(i, "sku", e.target.value)} placeholder="AUTO nếu trống" />
                </div>
                <div className="space-y-1">
                  <Label>Barcode</Label>
                  <Input value={v.barcode} onChange={(e) => updateVariant(i, "barcode", e.target.value)} placeholder="AUTO EAN-13" />
                </div>
                <div className="space-y-1">
                  <Label>Giá vốn</Label>
                  <Input type="number" min={0} value={v.cost_price} onChange={(e) => updateVariant(i, "cost_price", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Giá bán</Label>
                  <Input type="number" min={0} value={v.sell_price} onChange={(e) => updateVariant(i, "sell_price", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Giá sỉ</Label>
                  <Input type="number" min={0} value={v.wholesale_price} onChange={(e) => updateVariant(i, "wholesale_price", e.target.value)} />
                </div>
                <div className="flex items-center gap-4 sm:col-span-2">
                  <label className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={v.serial_tracked} onChange={(e) => updateVariant(i, "serial_tracked", e.target.checked)} />
                    Theo serial
                  </label>
                  <label className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={v.is_active} onChange={(e) => updateVariant(i, "is_active", e.target.checked)} />
                    Hoạt động
                  </label>
                  {variants.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="ml-auto text-destructive" onClick={() => removeVariant(i)}>
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

        <div className="flex gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Đang lưu…" : isEdit ? "Cập nhật" : "Tạo sản phẩm"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate({ to: "/products" })}>
            Huỷ
          </Button>
          {isEdit && canDelete && (
            <Button
              type="button"
              variant="destructive"
              className="ml-auto"
              onClick={handleDelete}
              disabled={deleteMut.isPending}
            >
              <Trash2 size={16} />
              {deleteMut.isPending ? "Đang xóa…" : "Xóa sản phẩm"}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
